import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "ticket-container" }
const _hoisted_2 = {
  key: 0,
  class: "ticket-container__loading-spinner"
}
const _hoisted_3 = {
  key: 1,
  class: "ticket-container__section"
}

import { computed, provide, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import EditTicketContent from '@/components/features/tickets/EditTicketContent.vue'
import EditTicketFooter from '@/components/features/tickets/EditTicketFooter.vue'
import EditTicketHeader from '@/components/features/tickets/EditTicketHeader.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { UpdateTicket, archiveTicket, deleteTicket, updateTicket } from '@/services/tickets.service'

interface EMITS {
  (e: 'closeEditTicketModal', val: boolean)
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ModalEditTicket',
  emits: ["closeEditTicketModal"],
  setup(__props, { emit: __emit }) {

const emits = __emit

const { t } = useI18n()
const store = useStore()
const piniaStore = useGeneralStore()

const formData = reactive<UpdateTicket>({
  externalId: null,
  title: null,
  description: null,
  categoryId: null,
  typeId: null,
  priority: null,
  status: null,
  startDate: null,
  dueDate: null
  // assignee: null
})
provide<UpdateTicket>('formDataEditErrand', formData)
const selectedTicket = computed(() => piniaStore.selectedTicket)

const loadingTicket = ref(true)
const selectedTab = ref('overview')
const showConfirmDelete = ref(false)

const selectedTicketNumber = computed(
  () =>
    selectedTicket.value?.externalId ?? selectedTicket.value?.ticketNumber?.toString() ?? undefined
)

function closeModal() {
  piniaStore.setSelectedTicket(null)
  emits('closeEditTicketModal', false)
}
async function archiveSelectedTicket() {
  if (selectedTicket.value)
    try {
      const ticket = await archiveTicket(selectedTicket.value?.id)
      if (ticket) {
        setInfoMessage('success', t('general__data__ticket-archived'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== ticket.id)
        piniaStore.setTicketsList(ticketsArray)
        piniaStore.setSelectedTicket(null)
        closeModal()
      }
    } catch (error) {
      setInfoMessage('error', 'general__data__ticket-archive-failed')
    }
}
async function deleteSelectedTicket() {
  if (selectedTicket.value)
    try {
      const res = await deleteTicket(selectedTicket.value?.id)
      if (res >= 200 && res < 300) {
        setInfoMessage('success', t('general__data__ticket-deleted'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== selectedTicket.value?.id)
        piniaStore.setTicketsList(ticketsArray)
        piniaStore.setSelectedTicket(null)
        closeModal()
      }
    } catch (error) {
      setInfoMessage('error', t('general__data__ticket-delete-failed'))
    }
}
function fillSelectedTicketForm(selectedTicket) {
  formData.externalId = selectedTicket.externalId ?? null
  formData.title = selectedTicket.title ?? null
  formData.description = selectedTicket.description ?? null
  formData.categoryId = selectedTicket.categoryId ?? null
  formData.typeId = selectedTicket.typeId ?? null
  formData.priority = selectedTicket.priority ?? null
  formData.status = selectedTicket.status ?? null
  formData.startDate = selectedTicket.startDate ?? null
  formData.dueDate = selectedTicket.dueDate ?? null
  // formData.assignee = selectedTicket.assignee ?? ''
}
function setInfoMessage(type: string, message: string) {
  store.commit('setInfoMessage', { message, type })
}

async function updateSelectedTicket() {
  // all form fields are optional for update
  if (selectedTicket.value && formData)
    try {
      const ticket = await updateTicket(selectedTicket.value.id, formData)
      if (ticket) {
        setInfoMessage('success', t('general__text__update-succeded'))
        // replace updated ticket in store
        const ticketsArray = piniaStore.ticketsList.map((t) => (t.id === ticket.id ? ticket : t))
        piniaStore.setTicketsList(ticketsArray)
        piniaStore.setSelectedTicket(ticket)
        closeModal()
      }
    } catch (error) {
      setInfoMessage('error', t('general__text__update-failed'))
    }
}

watch(
  selectedTicket,
  (newValue) => {
    if (newValue) {
      fillSelectedTicketForm(newValue)
    }
    loadingTicket.value = false
  },
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LayoutOverlay, { onCloseOverlay: closeModal }, {
    default: _withCtx(() => [
      (showConfirmDelete.value)
        ? (_openBlock(), _createBlock(OverlayCard, {
            key: 0,
            backgroundTransparent: true,
            onCloseOverlay: _cache[1] || (_cache[1] = ($event: any) => (showConfirmDelete.value = false))
          }, {
            default: _withCtx(() => [
              _createVNode(ConfirmDeleteAttachmentCard, {
                buttonText: _unref(t)('general__text__delete') + ' ' + _unref(t)('general__data__ticket').toLowerCase(),
                onCloseOverlay: _cache[0] || (_cache[0] = ($event: any) => (showConfirmDelete.value = false)),
                onConfirmDelete: deleteSelectedTicket
              }, null, 8, ["buttonText"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("main", _hoisted_1, [
        _createVNode(EditTicketHeader, {
          loadingTicket: loadingTicket.value,
          onArchiveTicket: archiveSelectedTicket,
          onCloseEditTicketModal: closeModal,
          onDeleteTicket: _cache[2] || (_cache[2] = ($event: any) => (showConfirmDelete.value = true))
        }, null, 8, ["loadingTicket"]),
        (loadingTicket.value)
          ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
              _createVNode(PulseLoader, {
                color: "#3f46d6",
                size: "16px"
              })
            ]))
          : (_openBlock(), _createElementBlock("section", _hoisted_3, [
              _createVNode(DividerHorizontal, { style: {"margin-bottom":"16px"} }),
              _createVNode(EditTicketContent, {
                class: "ticket-container__content",
                ticketNumber: selectedTicketNumber.value,
                onSelectedTab: _cache[3] || (_cache[3] = ($event: any) => (selectedTab.value = $event))
              }, null, 8, ["ticketNumber"])
            ])),
        (!loadingTicket.value && selectedTab.value === 'overview')
          ? (_openBlock(), _createBlock(EditTicketFooter, {
              key: 2,
              class: "ticket-container__footer",
              createdDate: selectedTicket.value?.createdDate ?? '',
              lastModifiedDate: selectedTicket.value?.lastModifiedDate ?? '',
              onCancelEditTicket: closeModal,
              onUpdateTicket: updateSelectedTicket
            }, null, 8, ["createdDate", "lastModifiedDate"]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})